import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf'

type Props = {
  src: string
  title?: string
  fileType?: string
}

type PreviewType = 'image' | 'pdf' | 'unsupported'

const PDF_WORKER_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js'
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png']

pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC

const getFileExtension = (value: string = ''): string => {
  const valueWithoutQuery = value.split('?')[0].split('#')[0]
  const fileName = valueWithoutQuery.substring(valueWithoutQuery.lastIndexOf('/') + 1)
  const dotIndex = fileName.lastIndexOf('.')
  if (dotIndex === -1) return ''

  return fileName.substring(dotIndex + 1).toLowerCase()
}

const getPreviewType = ({ src, title = '', fileType = '' }: Props): PreviewType => {
  const normalizedFileType = fileType.toLowerCase()
  const extension = getFileExtension(title) || getFileExtension(src)

  if (
    normalizedFileType.indexOf('pdf') >= 0 ||
    extension === 'pdf' ||
    src.indexOf('data:application/pdf') === 0
  ) {
    return 'pdf'
  }

  if (
    normalizedFileType.indexOf('image/') === 0 ||
    IMAGE_EXTENSIONS.includes(extension) ||
    src.indexOf('data:image/') === 0
  ) {
    return 'image'
  }

  return 'unsupported'
}

const FilePreview = (props: Props) => {
  const { src, title = '', fileType = '' } = props
  const previewType = getPreviewType({ src, title, fileType })

  if (previewType === 'pdf') {
    return <PdfPreview src={src} title={title} />
  }

  if (previewType === 'image') {
    return <ImageViewer alt={title} src={src} />
  }

  return (
    <UnsupportedPreview>
      <PreviewTitle>{title || 'ไม่สามารถแสดงตัวอย่างไฟล์นี้ได้'}</PreviewTitle>
      <PreviewText>ไม่รองรับการแสดงตัวอย่างไฟล์ประเภทนี้</PreviewText>
    </UnsupportedPreview>
  )
}

const PdfPreview = ({ src, title }: { src: string; title: string }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const renderTasks = useRef<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let isCancelled = false
    let loadingTask = null as any

    const clearRenderTasks = () => {
      renderTasks.current.forEach(task => {
        if (task && typeof task.cancel === 'function') {
          task.cancel()
        }
      })
      renderTasks.current = []
    }

    const renderPdf = async () => {
      const container = containerRef.current
      if (!container || !src) return

      clearRenderTasks()
      container.innerHTML = ''
      setIsLoading(true)
      setError('')

      try {
        loadingTask = pdfjsLib.getDocument(src)
        const pdf = await loadingTask.promise

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          if (isCancelled) return

          const page = await pdf.getPage(pageNumber)
          const baseViewport = page.getViewport({ scale: 1 })
          const containerWidth = Math.min(container.clientWidth || 760, 760)
          const scale = containerWidth / baseViewport.width
          const viewport = page.getViewport({ scale })
          const outputScale = Math.min(window.devicePixelRatio || 1, 2)
          const pageWrapper = document.createElement('div')
          const canvas = document.createElement('canvas')
          const context = canvas.getContext('2d')

          if (!context) {
            throw new Error('Canvas is not supported')
          }

          pageWrapper.className = 'pdf-preview-page'
          canvas.width = Math.floor(viewport.width * outputScale)
          canvas.height = Math.floor(viewport.height * outputScale)
          canvas.style.width = `${viewport.width}px`
          canvas.style.height = `${viewport.height}px`

          pageWrapper.appendChild(canvas)
          container.appendChild(pageWrapper)

          const renderTask = page.render({
            canvasContext: context,
            viewport,
            transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined,
          })
          renderTasks.current.push(renderTask)
          await renderTask.promise
        }

        if (!isCancelled) {
          setIsLoading(false)
        }
      } catch (err) {
        if (!isCancelled) {
          setError('ไม่สามารถแสดงตัวอย่างไฟล์ PDF ได้')
          setIsLoading(false)
        }
      }
    }

    renderPdf()

    return () => {
      isCancelled = true
      clearRenderTasks()
      if (loadingTask && typeof loadingTask.destroy === 'function') {
        loadingTask.destroy()
      }
    }
  }, [src])

  return (
    <PdfPreviewWrapper>
      {isLoading && <PreviewStatus>กำลังโหลดไฟล์ PDF...</PreviewStatus>}
      {error && (
        <UnsupportedPreview>
          <PreviewTitle>{title}</PreviewTitle>
          <PreviewText>{error}</PreviewText>
        </UnsupportedPreview>
      )}
      <PdfPages ref={containerRef} />
    </PdfPreviewWrapper>
  )
}

const ImageViewer = styled.img`
  max-width: 800px;
  width: 100%;
`

const PdfPreviewWrapper = styled.div`
  width: 860px;
  max-width: 92vw;
  max-height: 82vh;
  overflow: auto;
  background: #f4f4f4;
  padding: 16px;
`

const PdfPages = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .pdf-preview-page {
    background: #ffffff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.16);
    line-height: 0;
  }
`

const UnsupportedPreview = styled.div`
  width: 420px;
  max-width: 90vw;
  background: #ffffff;
  padding: 24px;
  text-align: center;
`

const PreviewTitle = styled.div`
  color: #333333;
  font-size: 14px;
  font-weight: 600;
  word-break: break-word;
`

const PreviewText = styled.div`
  color: #666666;
  font-size: 13px;
  margin-top: 8px;
`

const PreviewStatus = styled.div`
  color: #333333;
  font-size: 13px;
  margin-bottom: 12px;
  text-align: center;
`

export default FilePreview
